import React, { useState, useEffect } from 'react';
import api from '../../utils/api';
import Loader from '../../components/common/Loader';
import './HomeAlternativeManagement.css';

const HomeAlternativeManagement = () => {
  const [sections, setSections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [editData, setEditData] = useState({});
  const [newImage, setNewImage] = useState(null);
  const [imagePreview, setImagePreview] = useState('');
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    fetchSections();
  }, []);

  const fetchSections = async () => {
    try {
      const response = await api.get('/admin/home-alternative');
      setSections(response.data.data);
    } catch (error) {
      console.error('Fetch sections error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (section) => {
    setEditingId(section.id);
    setEditData({
      title: section.title,
      subtitle: section.subtitle,
      description: section.description,
      button_text: section.button_text,
      button_link: section.button_link,
      background_color: section.background_color,
      text_color: section.text_color
    });
    setNewImage(null);
    setImagePreview('');
  };

  const handleCancel = () => {
    setEditingId(null);
    setEditData({});
    setNewImage(null);
    setImagePreview('');
  };

  const handleImageSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      setNewImage(file);
      setImagePreview(URL.createObjectURL(file));
    }
  };

  const handleSave = async (id) => {
    try {
      setUploading(true);

      const formData = new FormData();
      formData.append('title', editData.title);
      formData.append('subtitle', editData.subtitle);
      formData.append('description', editData.description || '');
      formData.append('button_text', editData.button_text);
      formData.append('button_link', editData.button_link);
      formData.append('background_color', editData.background_color || '#FFFFFF');
      formData.append('text_color', editData.text_color || '#1A1A1A');

      if (newImage) {
        formData.append('image', newImage);
      }

      await api.put(`/admin/home-alternative/${id}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      await fetchSections();
      setEditingId(null);
      setEditData({});
      setNewImage(null);
      setImagePreview('');
      alert('Section updated successfully!');
    } catch (error) {
      console.error('Update error:', error);
      alert('Failed to update: ' + (error.response?.data?.error || error.message));
    } finally {
      setUploading(false);
    }
  };

  if (loading) return <Loader fullscreen />;

  const getSectionLabel = (section) => {
    const labels = {
      'hero_banner': 'Hero Banner',
      'category_women': 'Women Category',
      'category_men': 'Men Category',
      'category_accessories': 'Accessories Category',
      'promo_banner': 'Promotional Banner'
    };
    return labels[section] || section;
  };

  return (
    <div className="home-alt-management">
      <div className="page-header">
        <h1 className="page-title">🎨 Alternative Home Page</h1>
        <p className="page-subtitle">Manage your alternative home page design</p>
        <a 
          href="http://localhost:3000/home-alt" 
          target="_blank" 
          rel="noopener noreferrer"
          className="preview-link"
        >
          👁️ Preview Live Page
        </a>
      </div>

      <div className="sections-grid">
        {sections.map((section) => (
          <div key={section.id} className="section-card">
            <div className="section-header">
              <h2 className="section-label">{getSectionLabel(section.section)}</h2>
              <span className="section-id">ID: {section.id}</span>
            </div>

            <div className="section-preview">
              <img
                src={editingId === section.id && imagePreview
                  ? imagePreview
                  : (section.image_url.startsWith('http')
                    ? section.image_url
                    : `https://kid-o.ge${section.image_url}`)}
                alt={section.title}
                className="section-image"
              />
            </div>

            {editingId === section.id ? (
              <div className="edit-form">
                <div className="form-group">
                  <label>Image</label>
                  <div className="upload-button-container">
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleImageSelect}
                      id={`image-${section.id}`}
                      style={{ display: 'none' }}
                    />
                    <label htmlFor={`image-${section.id}`} className="upload-button">
                      📷 Choose Image
                    </label>
                    {newImage && (
                      <span className="file-selected">✓ {newImage.name}</span>
                    )}
                  </div>
                </div>

                <div className="form-group">
                  <label>Title</label>
                  <input
                    type="text"
                    value={editData.title}
                    onChange={(e) => setEditData({ ...editData, title: e.target.value })}
                    className="form-input"
                  />
                </div>

                <div className="form-group">
                  <label>Subtitle</label>
                  <input
                    type="text"
                    value={editData.subtitle}
                    onChange={(e) => setEditData({ ...editData, subtitle: e.target.value })}
                    className="form-input"
                  />
                </div>

                <div className="form-group">
                  <label>Description</label>
                  <textarea
                    value={editData.description}
                    onChange={(e) => setEditData({ ...editData, description: e.target.value })}
                    className="form-textarea"
                    rows="3"
                  />
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>Button Text</label>
                    <input
                      type="text"
                      value={editData.button_text}
                      onChange={(e) => setEditData({ ...editData, button_text: e.target.value })}
                      className="form-input"
                    />
                  </div>

                  <div className="form-group">
                    <label>Button Link</label>
                    <input
                      type="text"
                      value={editData.button_link}
                      onChange={(e) => setEditData({ ...editData, button_link: e.target.value })}
                      className="form-input"
                      placeholder="/shop"
                    />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>Background Color</label>
                    <div className="color-input-wrapper">
                      <input
                        type="color"
                        value={editData.background_color}
                        onChange={(e) => setEditData({ ...editData, background_color: e.target.value })}
                        className="color-picker"
                      />
                      <input
                        type="text"
                        value={editData.background_color}
                        onChange={(e) => setEditData({ ...editData, background_color: e.target.value })}
                        className="color-text"
                        placeholder="#FFFFFF"
                      />
                    </div>
                  </div>

                  <div className="form-group">
                    <label>Text Color</label>
                    <div className="color-input-wrapper">
                      <input
                        type="color"
                        value={editData.text_color}
                        onChange={(e) => setEditData({ ...editData, text_color: e.target.value })}
                        className="color-picker"
                      />
                      <input
                        type="text"
                        value={editData.text_color}
                        onChange={(e) => setEditData({ ...editData, text_color: e.target.value })}
                        className="color-text"
                        placeholder="#1A1A1A"
                      />
                    </div>
                  </div>
                </div>

                <div className="form-actions">
                  <button
                    onClick={() => handleSave(section.id)}
                    className="btn-save"
                    disabled={uploading}
                  >
                    {uploading ? 'Saving...' : '💾 Save Changes'}
                  </button>
                  <button onClick={handleCancel} className="btn-cancel">
                    ✕ Cancel
                  </button>
                </div>
              </div>
            ) : (
              <div className="section-content">
                <div className="content-preview">
                  <h3>{section.title}</h3>
                  <p className="subtitle">{section.subtitle}</p>
                  {section.description && <p className="description">{section.description}</p>}
                  <div className="button-preview">{section.button_text}</div>
                </div>

                <button onClick={() => handleEdit(section)} className="btn-edit">
                  ✏️ Edit Section
                </button>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default HomeAlternativeManagement;